import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Store, Webhook, AlertCircle, CheckCircle2, Send, Power } from 'lucide-react';

export default function ShopifySettings({ selectedTeam }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSettings, setHasSettings] = useState(false);
  const [team, setTeam] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [formData, setFormData] = useState({
    shop_domain: '',
    api_key: '',
    api_secret: '',
    webhook_secret: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (selectedTeam) {
        await loadSettings();
        await loadTeamInfo();
      }
    };
    fetchData();
  }, [selectedTeam]);

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get(`/teams/${selectedTeam}/shopify/settings`);
      setFormData({
        shop_domain: response.data.shop_domain,
        api_key: response.data.api_key,
        api_secret: response.data.api_secret,
        webhook_secret: response.data.webhook_secret
      });
      setEnabled(response.data.enabled !== false);
      setHasSettings(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setHasSettings(false);
      } else {
        toast.error('Ayarlar yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTeamInfo = async () => {
    try {
      const teamRes = await axiosInstance.get('/teams/my-teams');
      const currentTeam = teamRes.data.find(t => t.id === selectedTeam);
      setTeam(currentTeam);
    } catch (error) {
      console.error('Failed to load team info:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (hasSettings) {
        await axiosInstance.put(`/teams/${selectedTeam}/shopify/settings`, formData);
        toast.success('Ayarlar güncellendi');
      } else {
        await axiosInstance.post(`/teams/${selectedTeam}/shopify/settings`, formData);
        toast.success('Ayarlar kaydedildi');
        setHasSettings(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    if (!hasSettings) return;
    
    try {
      const newEnabled = !enabled;
      await axiosInstance.put(`/teams/${selectedTeam}/shopify/settings`, { enabled: newEnabled });
      setEnabled(newEnabled);
      toast.success(newEnabled ? 'Shopify aktarımı açıldı' : 'Shopify aktarımı kapatıldı');
    } catch (error) {
      toast.error('Durum değiştirilemedi');
    }
  };

  const webhookUrl = `${window.location.origin}/api/shopify/webhook/product-created`;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Store className="w-8 h-8 text-purple-600" />
          Shopify Entegrasyonu
        </h1>
        <p className="mt-2 text-gray-600">
          Shopify mağazanızdan otomatik ürün paylaşımı yapın
        </p>
      </div>

      {/* Enable/Disable Toggle */}
      {hasSettings && (
        <Card className={`mb-6 ${enabled ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Power className={`w-6 h-6 ${enabled ? 'text-green-600' : 'text-red-600'}`} />
                <div>
                  <h3 className="font-semibold text-gray-900">Shopify Aktarımı</h3>
                  <p className="text-sm text-gray-600">
                    {enabled ? 'Yeni ürünler otomatik olarak Telegram\'a gönderilecek' : 'Otomatik gönderim kapalı'}
                  </p>
                </div>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={toggleEnabled}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Webhook URL Info */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Webhook className="w-5 h-5 text-blue-600" />
            Webhook URL
          </CardTitle>
          <CardDescription>
            Bu URL&apos;yi Shopify admin panelinde webhook olarak ekleyin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-white p-4 rounded-lg border border-blue-200">
            <code className="text-sm text-blue-900 break-all">{webhookUrl}</code>
          </div>
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-700">
              <strong>Event:</strong> Product creation
            </p>
            <p className="text-sm text-gray-700">
              <strong>Format:</strong> JSON
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Settings Form */}
      <Card>
        <CardHeader>
          <CardTitle>Shopify Ayarları</CardTitle>
          <CardDescription>
            API bilgilerinizi girin
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Shop Domain */}
            <div>
              <Label htmlFor="shop_domain">Shop Domain *</Label>
              <Input
                id="shop_domain"
                type="text"
                placeholder="your-store.myshopify.com"
                value={formData.shop_domain}
                onChange={(e) => setFormData({ ...formData, shop_domain: e.target.value })}
                required
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Örnek: delfieestore.myshopify.com
              </p>
            </div>

            {/* API Key */}
            <div>
              <Label htmlFor="api_key">API Key *</Label>
              <Input
                id="api_key"
                type="text"
                placeholder="shpat_..."
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                required
                className="mt-2"
              />
            </div>

            {/* API Secret */}
            <div>
              <Label htmlFor="api_secret">API Secret *</Label>
              <Input
                id="api_secret"
                type="password"
                placeholder="shpss_..."
                value={formData.api_secret}
                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                required
                className="mt-2"
              />
            </div>

            {/* Webhook Secret */}
            <div>
              <Label htmlFor="webhook_secret">Webhook Secret *</Label>
              <Input
                id="webhook_secret"
                type="password"
                placeholder="Webhook signing secret"
                value={formData.webhook_secret}
                onChange={(e) => setFormData({ ...formData, webhook_secret: e.target.value })}
                required
                className="mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shopify webhook ayarlarından alın
              </p>
            </div>

            {/* Channel Info */}
            <div className="border-t pt-6">
              <Label className="flex items-center gap-2 mb-4">
                <Send className="w-4 h-4" />
                Otomatik Kanal Gönderimi
              </Label>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 mb-3">
                  <CheckCircle2 className="w-4 h-4 inline mr-2" />
                  Shopify&apos;dan gelen ürünler <strong>ürün yükleme</strong> ile aynı mantıkta otomatik olarak şu kanallara gönderilir:
                </p>
                <ul className="text-sm text-green-700 space-y-1 ml-6 list-disc">
                  <li><strong>Ana Kanal</strong> - Tüm ürünler</li>
                  <li><strong>Marka Kanalı</strong> - Eşleşen marka varsa</li>
                  <li><strong>Kategori Kanalı</strong> - Eşleşen kategori varsa</li>
                  <li><strong>SALE Kanalı</strong> - İndirimli ürünler için</li>
                </ul>
              </div>
              
              {!team?.channel_id && (
                <p className="text-sm text-amber-600 flex items-center gap-2 mt-4">
                  <AlertCircle className="w-4 h-4" />
                  Ana kanal tanımlanmamış. Bot Ayarları sayfasından kanal ID ekleyin.
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Kaydediliyor...' : hasSettings ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="mt-6 border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg">Kurulum Talimatları</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">1. Shopify Admin Paneline Gidin</h3>
            <p className="text-sm text-gray-600">
              Settings → Apps and sales channels → Develop apps
            </p>

            <h3 className="font-semibold text-sm mt-4">2. API Credentials Oluşturun</h3>
            <p className="text-sm text-gray-600">
              Create an app → Admin API access token alın
            </p>

            <h3 className="font-semibold text-sm mt-4">3. Webhook Ekleyin</h3>
            <p className="text-sm text-gray-600">
              Settings → Notifications → Webhooks → Product creation
            </p>
            <p className="text-sm text-gray-600">
              URL&apos;yi yukarıdaki kutucuktan kopyalayın
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
